import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Landmark, Power, Pencil, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageGuideButton } from "@/components/PageGuideButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EmptyState, PageLoading, PageHeader } from "@/components/shared";
import api from "@/lib/api";

const blank = { country_code: "", country_name: "", company_name_from_letter: "", company_name_to_letter: "", finanzamt_name: "", jurisdiction_type: "country", legal_source: "", source_url: "", valid_from: "", valid_until: "", notes: "" };

export default function AdminTaxOffices() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/reference-data/tax-offices").then((r) => setRows(r.data.rules));
  useEffect(() => { load(); }, []);
  const [aiBusy, setAiBusy] = useState(false);
  const aiFill = async () => {
    setAiBusy(true);
    try { const r = await api.post("/admin/reference-data/tax-offices/ai-fill"); toast.success(t("ai.taxFilled", { count: r.data.count })); load(); }
    catch { toast.error("Error"); } finally { setAiBusy(false); }
  };
  const toggle = async (r) => { try { await api.delete(`/admin/reference-data/tax-offices/${r.id}`); load(); } catch { toast.error("Error"); } };

  return (
    <div data-testid="admin-tax-offices">
      <PageHeader title={t("tax.title")} subtitle={t("tax.subtitle")} action={
        <div className="flex flex-wrap gap-2">
          <PageGuideButton pageKey="finanzamt" />
          <Button onClick={aiFill} disabled={aiBusy} variant="outline" data-testid="tax-ai-fill-btn" className="rounded-full border-cyan text-cyan-dark hover:bg-cyan/10">{aiBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />} {t("ai.taxFill")}</Button>
          <Button onClick={() => setEdit(blank)} data-testid="add-tax-rule-btn" className="rounded-full bg-navy text-white hover:bg-navy-muted"><Plus className="mr-1 h-4 w-4" /> {t("tax.add")}</Button>
        </div>
      } />
      {rows === null ? <PageLoading /> : rows.length === 0 ? <EmptyState icon={Landmark} title={t("tax.empty")} subtitle={t("tax.emptyHint")} /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="tax-rules-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("tax.country")}</th><th className="px-4 py-3">{t("tax.letters")}</th><th className="px-4 py-3">Finanzamt</th><th className="px-4 py-3">{t("tax.source")}</th><th className="px-4 py-3">{t("adm.status")}</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`tax-rule-${r.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{r.country_code}<span className="ml-1 text-xs font-normal text-slate-400">{r.country_name}</span></td>
                  <td className="px-4 py-3 text-slate-600">{r.company_name_from_letter ? `${r.company_name_from_letter}–${r.company_name_to_letter || r.company_name_from_letter}` : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.finanzamt_name}</td>
                  <td className="px-4 py-3 text-slate-500">{r.source_url ? <a href={r.source_url} target="_blank" rel="noreferrer" className="text-cyan-dark underline">{t("tax.viewSource")}</a> : (r.legal_source || "—")}</td>
                  <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${r.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-slate-50 text-slate-400"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEdit(r)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy" data-testid={`tax-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => toggle(r)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy" data-testid={`tax-toggle-${r.id}`}><Power className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RuleDialog rule={edit} onClose={() => setEdit(null)} onDone={load} t={t} />
    </div>
  );
}

function RuleDialog({ rule, onClose, onDone, t }) {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (rule) setF({ ...blank, ...rule }); }, [rule]);
  if (!rule) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.country_code.trim() || !f.finanzamt_name.trim()) { toast.error(t("tax.req")); return; }
    setBusy(true);
    try {
      if (rule.id) await api.put(`/admin/reference-data/tax-offices/${rule.id}`, f);
      else await api.post("/admin/reference-data/tax-offices", f);
      toast.success(t("sub.added")); onDone(); onClose();
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };
  const F = ({ label, k, ph, type }) => (<div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label><Input type={type || "text"} value={f[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`tax-field-${k}`} /></div>);
  return (
    <Dialog open={!!rule} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tax-rule-dialog">
        <DialogHeader><DialogTitle>{rule.id ? t("tax.editRule") : t("tax.add")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label={t("tax.countryCode") + " *"} k="country_code" ph="SI" />
          <F label={t("tax.countryName")} k="country_name" ph="Slovenia" />
          <F label={t("tax.fromLetter")} k="company_name_from_letter" ph="A" />
          <F label={t("tax.toLetter")} k="company_name_to_letter" ph="Z" />
          <div className="col-span-2"><F label="Finanzamt *" k="finanzamt_name" ph="Finanzamt Oranienburg" /></div>
          <F label={t("tax.validFrom")} k="valid_from" type="date" />
          <F label={t("tax.validUntil")} k="valid_until" type="date" />
          <div className="col-span-2"><F label={t("tax.legalSource")} k="legal_source" /></div>
          <div className="col-span-2"><F label={t("tax.sourceUrl")} k="source_url" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="tax-save-btn" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
